import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ChatBotModel } from '../handlers/ChatBotModel.ts'
import ChatIDB from "../handlers/chatDB.js";
import * as Constants from '../../../config/Constants.ts'

import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/GlobalStore.ts';


export const useChatbotDispatch = () => useDispatch<AppDispatch>();
export const useChatbotSelector = <T>(selector: (state: RootState) => T): T =>
    useSelector(selector);

export interface ChatBotState {
    byType: Record<string, ChatBotModel[]>;
    loading: boolean;
    error: string | null;
    pageByType: Record<string, number>;
    hasMoreByType: Record<string, boolean>;
    activeChatBotTab: string;
    isChatBotOpen: boolean;
    isReceiverLoader: boolean,
    typingMessageEffect: boolean

}

const initialState: ChatBotState = {
    byType: {},
    loading: false,
    error: null,
    pageByType: {},
    hasMoreByType: {},
    activeChatBotTab: Constants.REQ_CHATBOT_TAB_TYPE.SUPPORT,
    isChatBotOpen: false,
    isReceiverLoader: false,
    typingMessageEffect: false
};

export const loadChatBot = createAsyncThunk<
    { data: ChatBotModel[]; chatBotType: string },
    string,
    { state: { chatbot: ChatBotState } }>(
        '',
        async (chatBotType, { getState }) => {

            const page = getState().chatbot.pageByType[chatBotType] || 1;
            const data = await ChatIDB.getAllMessages(chatBotType) || [];
            return { data, chatBotType: chatBotType };
        });

const chatbotSlice = createSlice({
    name: 'chatBot',
    initialState,
    reducers: {
        resetChatBot: () => initialState,

        setActiveChatBotTab(state, action: PayloadAction<string>) {
            state.activeChatBotTab = action.payload;
        },

        addMessage(state, action: PayloadAction<{ msg: ChatBotModel; type: string }>) {

            let { msg, type } = action.payload;

            let existing = state.byType[state.activeChatBotTab] || [];
            
            if (type === "bot") {

                const { chatid } = msg;
                state.byType[state.activeChatBotTab] = existing.map((message) => message.sender === 'user' && message.chatid === chatid
                        ? { ...message, isMsgReceived: true } : message );

                
                ChatIDB.updateMessageData(chatid);

                msg = { ...msg, chatid: Date.now() };  // Clone with new chatid

                existing = state.byType[state.activeChatBotTab] || [];
                state.byType[state.activeChatBotTab] = [...existing, ...[msg]];

                state.isReceiverLoader = false;
                state.typingMessageEffect = true;

            } else {
                state.byType[state.activeChatBotTab] = [...existing, ...[msg]];
                state.isReceiverLoader = true;
            }

            ChatIDB.addMessage(state.activeChatBotTab, msg);
        },

        openChatBot(state, action: PayloadAction<boolean>) {
            state.isChatBotOpen = action.payload;
        },

        clearChatBotStore(state) {
            state.byType = {};
        },
        setReceiverLoader(state, action: PayloadAction<boolean>) {
            state.isReceiverLoader = action.payload
        },
        setTypingMessageEffect(state, action: PayloadAction<boolean>) {
            state.typingMessageEffect = action.payload
        },
        userMessageReceived(state, action: PayloadAction<{ chatid: number }>) {
            const chatBotType = state.activeChatBotTab;
            const messages = state.byType[chatBotType] || [];
            state.byType[chatBotType] = messages.map((msg) =>msg.sender === 'user' && msg.chatid === action.payload.chatid
                    ? { ...msg, isMsgReceived: true, status: 'sent' }
                    : msg);
                    ChatIDB.updateMessageData(action.payload.chatid);
        },
        deleteMessageFromStore: (state, action: PayloadAction<number>) =>{
            const messages = state.byType[state.activeChatBotTab] || [];
            state.byType[state.activeChatBotTab] = messages.filter(msg =>msg.chatid !== action.payload);
            ChatIDB.deleteMessage(state.activeChatBotTab, action.payload); // 1. Remove from DB
        }

    },
    extraReducers: (builder) => {
        builder
            .addCase(loadChatBot.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loadChatBot.fulfilled, (state, { payload }) => {
                const { chatBotType: chatBotType, data } = payload;
                let existing = state.byType[chatBotType] || [];

                if (!state.hasMoreByType[chatBotType] && data.length === 0) {

                    const defaultBotMessage: ChatBotModel = {
                        sender: 'bot',
                        text: Constants.DEFAULT_BOT_MESSAGE,
                        time: new Date().toLocaleTimeString(),
                        chatid: Date.now(),
                        isMsgReceived: false,
                    };

                    existing = [...existing, defaultBotMessage];
                }

                state.byType[chatBotType] = [...existing, ...data];
                state.pageByType[chatBotType] = (state.pageByType[chatBotType] || 1) + 1;
                state.hasMoreByType[chatBotType] = data.length === 100;
                state.loading = false;
            })
            .addCase(loadChatBot.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message ?? 'Failed to load Chatbot';
            });
    },
});


export const {
    resetChatBot,
    setActiveChatBotTab,
    addMessage,
    openChatBot,
    clearChatBotStore,
    setReceiverLoader,
    setTypingMessageEffect,
    userMessageReceived,
    deleteMessageFromStore,
} = chatbotSlice.actions;
export default chatbotSlice.reducer;